import type { PlanType } from '@labrav/react-components'

import type { CloudMarketplace, links } from '../productsListing/types'

export enum ContractsListingActionTypes {
  SET_CONTRACTS = 'SET_CONTRACTS',
  UPDATE_ENTITLEMENTS_OR_DIMENSIONS = 'UPDATE_ENTITLEMENTS_OR_DIMENSIONS',
  CLEAR_CONTRACTS = 'CLEAR_CONTRACTS',
}

export type ListingStatus = 'EXPIRING_SOON' | 'EXPIRED'
export type Status = 'ACTIVE' | 'DEACTIVATED' | 'ARCHIVED' | 'PENDING'
export type OfferType = 'PUBLIC' | 'PRIVATE'
export type MeteringKeyName = 'entitlements' | 'dimensions' | 'plan'
export type DimensionOrEntitlementKey = 'entitlements' | 'dimensions'

export interface UpdateEntitlementsOrDimensionsValue {
  keyName: MeteringKeyName
  values: string
  meteringId: string
}

export interface ContractListingDimensionFields {
  dimensionId: string
  dimensionName: string
  dimensionLabel: string
  dimensionValueDataType: number | string | boolean
  meteringTimestamp?: string
  meteringId?: string
  meteringQuantityConsumed?: number
  unit?: string
  meteringLastUpdatedAt?: string
  meteringStatus?: Status
  id?: string
  label?: string
  name?: string
  unitOfMeasure?: string
  cloudDimensionName?: string
  pricePerUnit?: number
}

export interface Entitlement extends ContractListingDimensionFields {
  entitlementId: string
  entitlementValue?: string
  price?: number
  entitlementExpirationDate: string
  dimensionLabelDescription: string
  dimensionIsAdditionalUsageAllowed: boolean
  dimensionAdditionalUsageDescription?: string
}

export interface Entitlements {
  rows: Entitlement[]
}

export type ContractListingDimension = ContractListingDimensionFields

export interface ContractListingDimensions {
  rows: ContractListingDimension[]
}

export interface Plan {
  dimensions: ContractListingDimensionFields[]
  name: string
  type: string
  id: string
}

export interface Contract {
  id: string
  licenseArn?: string
  cloudMarketplaceContractId: string
  buyerCompanyName: string
  buyerId?: string
  buyerLogoUrl: string
  lastMeteringInfoSentAt?: string
  contractStartDate: string
  contractExpirationDate: string
  subscriptionEndDate: string
  listingStatus?: ListingStatus
  status?: Status
  createdAt?: string
  updatedAT?: string
  offerType?: OfferType
  cloudMarketplaceProductId: string
  productId: string
  productName?: string
  planId?: string
  cloudMarketplace?: string
  productType?: PlanType
  links?: links
  dimensions?: ContractListingDimensions
  entitlements?: Entitlements
  plan?: Plan
  cloudMarketplaceBuyerAccountId?: string
  planName?: string
}

export interface CloudMarketPlaceContract {
  dimensionId: string
  meteringQuantityConsumed: number
  meteringTimestamp?: string
}

export interface ContractsListingResponse {
  contracts: {
    rows: Contract[]
  }
  pageNumber: number
  pageSize: number
  totalCount: number
  sortBy: string
  sortOrder: string
  links: links & {
    nextPage: string
    previousPage: string
  }
}

export interface AwsMeteringsPayload {
  cloudMarketplace: CloudMarketplace
  timestamp: string
  dimensions: {
    labraSubscriptionId: string
    dimensionName: string
    quantity: number
  }[]
}

export interface ContractsListingState {
  contractsData: ContractsListingResponse
}

export type ContractsListingApiContracts = {
  rows: Contract[]
} & Record<string, unknown>

export type ContractsListingApiData = {
  contracts?: ContractsListingApiContracts
} & Record<string, unknown>

export type ContractsListingMeteringResponse = {
  data?: any
}

export const initialState: ContractsListingState = {
  contractsData: {
    contracts: {
      rows: [],
    },
    pageNumber: 0,
    pageSize: 0,
    totalCount: 0,
    sortBy: '',
    sortOrder: '',
    links: {
      nextPage: '',
      previousPage: '',
      self: '',
    },
  },
}

export type ContractsListingAction =
  | {
      type: ContractsListingActionTypes.SET_CONTRACTS
      payload: ContractsListingResponse
    }
  | {
      type: ContractsListingActionTypes.UPDATE_ENTITLEMENTS_OR_DIMENSIONS
      payload: {
        contractId: string
      } & UpdateEntitlementsOrDimensionsValue
    }
  | {
      type: ContractsListingActionTypes.CLEAR_CONTRACTS
    }
